import LoginError from '@root/assets/svg/Auth/LoginError';
import RadioButton from '@root/components/v2/ui-elements/RadioButton';
import TopOpenTooltip from '@root/components/v2/ui-elements/Tooltip/TopOpenTooltip';
import { Button } from '@root/components/v3/button';
import { DialogClose, DialogFooter, DialogHeader, DialogTitle } from '@root/components/v3/dialog';
import { Input } from '@root/components/v3/input';
import { Label } from '@root/components/v3/label';
import { Textarea } from '@root/components/v3/textarea';
import { axiosPost } from '@root/services/axios/axiosPost';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const DismissPopup = ({
  onClose,
  dismissDialogData,
  setDismissDialogData,
  bulkRemediation = false,
  previousJustification = '',
  isActionHistory = false,
  previousDismissDays = '0',
  from,
  isSelectAllEventDetails,
  isSelectAllEvent,
  setSelectEventType,
  setIsSelectAllEvent,
  environments,
}) => {
  const [justification, setJustification] = useState(previousJustification);
  const [justificationTime, setJustificationTime] = useState(previousDismissDays);
  const [isExceedjustificationTime, setIsExceedJustificationTime] = useState(false);
  const [timeSetting, setTimeSetting] = useState('set');
  const [showLoader, setShowLoader] = useState(false);
  const user = useSelector(state => state.user);
  const queryClient = useQueryClient();

  const handleJustificationTimeChange = e => {
    if (Number(e.target.value) <= 365 && Number(e.target.value >= 0)) setJustificationTime(e.target.value);
    if (Number(e.target.value) > 365) {
      setIsExceedJustificationTime(true);
    } else {
      setIsExceedJustificationTime(false);
    }
  };

  const handleActionChange = async () => {
    setShowLoader(true);
    let payload;
    if (from == 'k8') {
      payload = {
        user_email: user.info.user.email,
        cluster: dismissDialogData?.cluster,
        action: 'dismiss',
        image_digest: dismissDialogData?.image_digest,
        image: dismissDialogData?.image,
        rule_id: dismissDialogData?.rule_id,
        justification_time: timeSetting === 'infinite' ? 0 : parseInt(justificationTime, 10) || Date.now(),
        alert_id: dismissDialogData?.alert_id,
        justification_reason: justification,
        is_permanent: timeSetting === 'infinite',
        message: dismissDialogData?.message,
        environment: environments,
      };
      try {
        const res = await axiosPost('v1/kubernetes/runtime-alerts/update-action', payload, true);
        if (res) {
          setShowLoader(false);
        }
        if (res?.data && res.data.error === 0) {
          toast.success('Action updated succesfully!');
          setDismissDialogData(prev => ({ ...prev, selected: true }));
          queryClient.invalidateQueries(['get_kubernetes_runtime_alerts_list']);
        } else {
          toast.error('Something went wrong!');
        }
      } catch (error) {
        toast.error('Something went wrong!');
      } finally {
        onClose();
      }
    } else {
      if (bulkRemediation) {
        payload = {
          range: {
            to_date: isSelectAllEventDetails?.to_date,
            from_date: isSelectAllEventDetails?.from_date,
            timezone: isSelectAllEventDetails?.timezone,
          },
          scope: isSelectAllEventDetails?.scope,
          environments: isSelectAllEventDetails?.environments,
          cloud_provider: isSelectAllEventDetails?.cloud_provider,
          ...(isSelectAllEvent && {
            search_keyword: isSelectAllEventDetails?.search_keyword,
            event_filter: {
              all_resources: {
                service_category: isSelectAllEventDetails?.event_filter?.all_resources?.service_category,
                service: isSelectAllEventDetails?.event_filter?.all_resources?.service,
                resource_type: isSelectAllEventDetails?.event_filter?.all_resources?.resource_type,
              },
              properties_filter: {
                cve_id: isSelectAllEventDetails?.event_filter?.properties_filter?.cve_id,
                evaluation: isSelectAllEventDetails?.event_filter?.properties_filter?.evaluation,
                risk: isSelectAllEventDetails?.event_filter?.properties_filter?.risk,
                environment_tag: isSelectAllEventDetails?.event_filter?.properties_filter?.environment_tag,
                event_type: isSelectAllEventDetails?.event_filter?.properties_filter?.event_type,
                master_rule_id: isSelectAllEventDetails?.event_filter?.properties_filter?.master_rule_id,
                cq_shield_priority: isSelectAllEventDetails?.event_filter?.properties_filter?.cq_shield_priority,
                event_name: isSelectAllEventDetails?.event_filter?.properties_filter?.event_name,
                principal_arn: isSelectAllEventDetails?.event_filter?.properties_filter?.principal_arn,
                source_ip_address: isSelectAllEventDetails?.event_filter?.properties_filter?.source_ip_address,
                country: isSelectAllEventDetails?.event_filter?.properties_filter?.country,
                event_source: isSelectAllEventDetails?.event_filter?.properties_filter?.event_source,
                mitre_attack: isSelectAllEventDetails?.event_filter?.properties_filter?.mitre_attack,
                detection_risk: isSelectAllEventDetails?.event_filter?.properties_filter?.detection_risk,
                match: isSelectAllEventDetails?.event_filter?.properties_filter?.match,
                threat_action: isSelectAllEventDetails?.event_filter?.properties_filter?.threat_action,
                detected_date: {
                  start: isSelectAllEventDetails?.event_filter?.properties_filter?.detected_date.start,
                  end: isSelectAllEventDetails?.event_filter?.properties_filter?.detected_date.end,
                },
              },
            },
            threat_action: 'Dismiss',
            justification_reason: justification || '',
            justification_time: timeSetting === 'infinite' ? 0 : parseInt(justificationTime, 10) || Date.now(),
            is_permanent: timeSetting === 'infinite',
            user: user.info.user.email,
          }),
          tab: isActionHistory ? 'action_history' : 'threat',
          is_all_events_selected: isSelectAllEvent ? true : false,
          unique_id: window.localStorage.getItem('uniqueUserId'),
          data: isSelectAllEvent
            ? []
            : dismissDialogData?.map(data => ({
                event_id: data.event_id,
                environment_id: data.environment_id,
                master_rule_id: data.master_rule_id,
                principal_arn: data.principal_arn,
                justification_reason: justification || '',
                event_name: data.event_name,
                justification_time: timeSetting === 'infinite' ? 0 : parseInt(justificationTime, 10) || Date.now(),
                justification_active: true,
                is_permanent: timeSetting === 'infinite',
                threat_action: 'Dismiss',
                user: user.info.user.email,
                source_ips: data?.ips?.[0] || '',
              })),
        };
      } else {
        payload = {
          range: {
            to_date: isSelectAllEventDetails?.to_date,
            from_date: isSelectAllEventDetails?.from_date,
            timezone: isSelectAllEventDetails?.timezone,
          },
          tab: isActionHistory ? 'action_history' : 'threat',
          scope: isSelectAllEventDetails?.scope,
          environments: isSelectAllEventDetails?.environments,
          cloud_provider: isSelectAllEventDetails?.cloud_provider,
          is_all_events_selected: false,
          unique_id: window.localStorage.getItem('uniqueUserId'),
          data: [
            {
              event_id: dismissDialogData.event_id,
              environment_id: dismissDialogData.environment_id,
              master_rule_id: dismissDialogData.master_rule_id,
              principal_arn: dismissDialogData.principal_arn,
              justification_reason: justification || '',
              event_name: dismissDialogData.event_name,
              justification_time: timeSetting === 'infinite' ? 0 : parseInt(justificationTime, 10) || Date.now(),
              justification_active: true,
              is_permanent: timeSetting === 'infinite',
              threat_action: 'Dismiss',
              user: user.info.user.email,
              source_ips: dismissDialogData?.ips?.[0] || '',
            },
          ],
        };
      }

      try {
        const res = await axiosPost('cloud_events_1.2/event_action_update', payload);
        if (res) {
          setShowLoader(false);
        }

        if (res?.data && !res?.data.error) {
          toast.success(res?.data?.message);

          if (bulkRemediation) {
            setDismissDialogData(prev => {
              return prev?.map(data => ({
                ...data,
                selected: 'Dismiss',
              }));
            });
          } else {
            setDismissDialogData(prev => ({ ...prev, selected: true }));
          }
          queryClient.invalidateQueries(['get_list_events_threats']);
          if (isActionHistory) {
            queryClient.invalidateQueries(['get_justified_events']);
          }
          onClose();
        } else {
          toast.error('Something went wrong!');
        }
      } catch (error) {
        console.error('Catch Error', error);
        toast.error('Something went wrong!');
      } finally {
        setSelectEventType('');
        setIsSelectAllEvent(false);
        onClose();
      }
    }
  };

  const handleChange = e => {
    const inputValue = e.target.value;
    if (inputValue.length <= 300) {
      setJustification(() => inputValue);
    } else {
      setJustification(() => inputValue.substring(0, 300));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-[#1a202c] text-sm font-semibold">Dismiss Finding</DialogTitle>
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogHeader>

      <div className="space-y-4">
        <div className="overflow-hidden text-[#484d56] text-ellipsis font-manrope text-sm font-medium flex flex-col gap-[7px]">
          <Label className="!text-sm">
            For how long?<span className="text-[#F62121]">*</span>:
          </Label>

          <div className="flex gap-[6px] items-center">
            <RadioButton checked={timeSetting === 'set'} onChange={() => setTimeSetting('set')} />
            For a certain amount of time{' '}
            <TopOpenTooltip
              title={
                <div className="p-[5px_10px]">
                  <span>Maximum 365 days are allowed</span>
                </div>
              }>
              <div className="w-[15px] h-5 flex items-center">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 11.2C8.87188 11.2 11.2 8.87188 11.2 6C11.2 3.12812 8.87188 0.8 6 0.8C3.12812 0.799999 0.800001 3.12812 0.800001 6C0.8 8.87188 3.12812 11.2 6 11.2ZM12 6C12 9.31371 9.31371 12 6 12C2.68629 12 2.34843e-07 9.31371 5.24537e-07 6C8.1423e-07 2.68629 2.68629 -8.1423e-07 6 -5.24537e-07C9.31371 -2.34843e-07 12 2.68629 12 6ZM6 9.62656C5.37909 9.62656 5.2 9.02091 5.2 8.8L5.2 5.44C5.2 5.21909 5.37909 4.82656 6 4.82656C6.62091 4.82656 6.8 5.21909 6.8 5.44L6.8 8.8C6.8 9.02091 6.62091 9.62656 6 9.62656Z"
                    fill="#718096"
                  />
                  <path
                    d="M5.2 3.62656C5.2 3.18473 5.55817 2.82656 6 2.82656C6.44183 2.82656 6.8 3.18473 6.8 3.62656C6.8 4.06839 6.44183 4.42656 6 4.42656C5.55817 4.42656 5.2 4.06839 5.2 3.62656Z"
                    fill="#718096"
                  />
                </svg>
              </div>
            </TopOpenTooltip>
            <Input
              value={justificationTime}
              onChange={handleJustificationTimeChange}
              type="number"
              max="365"
              min="0"
              className="rounded-[3px] border border-[#cbd5e0] p-1 h-[21px] text-center text-[#484d56] font-manrope text-sm font-medium m-0 w-16"
            />
            Days
          </div>

          {isExceedjustificationTime && (
            <div className="text-[#f62121] font-manrope text-xs font-medium">
              <small className="flex gap-1 items-center">
                <LoginError />
                <span className="text-xs font-medium">Maximum 365 days are allowed</span>
              </small>
            </div>
          )}

          <div className="flex gap-[6px] items-center">
            <RadioButton checked={timeSetting === 'infinite'} onChange={() => setTimeSetting('infinite')} />
            Forever
          </div>
        </div>

        <div>
          <div className="text-[#484d56] font-manrope text-xs font-medium flex mb-2">
            <Label className="flex-grow !text-xs">Justification</Label>
            <div>
              <span className="text-[#F84D4D]">*</span>Max 100 words
            </div>
          </div>

          <Textarea
            placeholder="I am dismissing the finding because..."
            onChange={handleChange}
            autoFocus
            value={justification}
            className="w-full"
          />
        </div>
      </div>

      <DialogFooter className="gap-[15px] py-0">
        <DialogClose asChild>
          <Button
            data-testid="click-events-table-dismisspopup-onclose-318"
            data-clickable="true"
            variant="outline"
            onClick={onClose}>
            Cancel
          </Button>
        </DialogClose>
        <Button
          data-testid="click-events-table-dismisspopup-button-328"
          data-clickable="true"
          disabled={
            isEmpty(justification) ||
            justification.length < 30 ||
            (timeSetting === 'set' && parseInt(justificationTime, 10) === 0)
          }
          onClick={showLoader ? () => {} : handleActionChange}>
          {showLoader && <Spinner animation="border" size="sm" />}
          Submit
        </Button>
      </DialogFooter>
    </>
  );
};

export default DismissPopup;
